import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Hash, RefreshCw, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtIDR } from "../lib/api";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

export default function SalesDuplicateNumbers() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState("");
  const [fixing, setFixing] = useState(false);
  const isOwner = user?.role === "owner";

  const load = useCallback(() => {
    setLoading(true);
    api.get("/sales/duplicate-numbers", { params: { page, limit: 20 } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const runBackfill = async () => {
    if (!window.confirm("Tautkan semua riwayat stok lama ke nota yang benar? Aman dijalankan berulang dan tidak mengubah jumlah stok.")) return;
    setFixing(true);
    try {
      const r = await api.post("/admin/migrate-legacy/backfill-sales-reference-id");
      toast.success(`${r.data.linked} riwayat tertaut · ${r.data.ambiguous} belum pasti · ${r.data.sales_not_found} nota tidak ditemukan`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setFixing(false); }
  };

  const renumber = async (sid, number) => {
    if (!window.confirm(`Beri nomor baru untuk transaksi ini? Nomor ${number} akan diganti dan referensi riwayat stoknya ikut diperbarui.`)) return;
    setBusy(sid);
    try {
      const r = await api.post(`/sales/${sid}/renumber`);
      toast.success(`${r.data.old_number} → ${r.data.new_number} (${r.data.movements_updated} riwayat diperbarui)`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-6" data-testid="dup-numbers-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl text-cocoa">Audit Nomor Kembar</h1>
          <p className="text-sm text-wmuted max-w-2xl">
            Nomor penjualan yang dipakai lebih dari satu transaksi — biasanya karena nomor baru bertabrakan
            dengan nomor hasil impor POS lama. Beri nomor baru pada transaksi yang lebih baru agar riwayat stok
            menunjuk ke nota yang tepat.
          </p>
        </div>
        <div className="flex gap-2">
          {isOwner && (
            <Button onClick={runBackfill} disabled={fixing} className="bg-cocoa hover:bg-cocoa-dark text-ivory" data-testid="dup-backfill-btn">
              <Wand2 className="w-4 h-4 mr-1.5" /> {fixing ? "Merapikan..." : "Rapikan Riwayat Sekali Klik"}
            </Button>
          )}
          <Button variant="outline" onClick={load} className="border-taupe text-cocoa" data-testid="dup-refresh-btn">
            <RefreshCw className="w-4 h-4 mr-1.5" /> Muat Ulang
          </Button>
        </div>      </div>

      {loading ? <div className="py-10 text-center text-wmuted">Memuat...</div>
        : !data?.groups?.length ? (
          <div className="rounded-lg border border-taupe/30 bg-white p-10 text-center text-wmuted" data-testid="dup-empty">
            Tidak ada nomor penjualan kembar. 🎉
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800" data-testid="dup-total">
              <AlertTriangle className="w-4 h-4" /> {data.total} nomor kembar ditemukan
            </div>

            <div className="space-y-4">
              {data.groups.map((g) => (
                <div key={g.number} className="rounded-lg border border-taupe/30 bg-white overflow-hidden" data-testid={`dup-group-${g.number}`}>
                  <div className="flex items-center gap-2 border-b border-taupe/20 bg-ivory px-4 py-2">
                    <Hash className="w-4 h-4 text-cocoa" />
                    <span className="font-medium text-cocoa">{g.number}</span>
                    <span className="text-xs text-wmuted">dipakai {g.count} transaksi</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="text-left text-wmuted">
                        <tr className="border-b border-taupe/20">
                          <th className="px-4 py-2 font-medium">Tanggal</th>
                          <th className="px-4 py-2 font-medium">Channel</th>
                          <th className="px-4 py-2 font-medium">Item</th>
                          <th className="px-4 py-2 font-medium text-right">Total</th>
                          <th className="px-4 py-2 font-medium text-right">Riwayat Stok</th>
                          <th className="px-4 py-2 font-medium text-right">Aksi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.sales.map((s) => (
                          <tr key={s.id} className="border-b border-taupe/10 last:border-0" data-testid={`dup-sale-${s.id}`}>
                            <td className="px-4 py-2 whitespace-nowrap">{s.date || "-"}</td>
                            <td className="px-4 py-2 whitespace-nowrap">{s.channel || s.source || "-"}{s.number_before && <span className="block text-xs text-amber-700">dulu: {s.number_before}</span>}</td>
                            <td className="px-4 py-2">{s.items || "-"}{s.item_count > 3 ? ` +${s.item_count - 3}` : ""}</td>
                            <td className="px-4 py-2 text-right whitespace-nowrap">{fmtIDR(s.grand_total)}</td>
                            <td className="px-4 py-2 text-right">{s.movements}</td>
                            <td className="px-4 py-2 text-right whitespace-nowrap">
                              <Button size="sm" variant="ghost" onClick={() => nav(`/sales?focus=${s.id}`)} className="text-cocoa" data-testid={`dup-open-${s.id}`}>Lihat Nota</Button>
                              {isOwner && (
                                <Button size="sm" variant="outline" disabled={busy === s.id} onClick={() => renumber(s.id, g.number)}
                                        className="ml-1 border-taupe text-cocoa" data-testid={`dup-renumber-${s.id}`}>
                                  {busy === s.id ? "..." : "Beri Nomor Baru"}
                                </Button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between text-sm text-wmuted">
              <span>Halaman {data.page} dari {data.pages}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="border-taupe text-cocoa" data-testid="dup-prev">Sebelumnya</Button>
                <Button size="sm" variant="outline" disabled={page >= (data.pages || 1)} onClick={() => setPage((p) => p + 1)} className="border-taupe text-cocoa" data-testid="dup-next">Berikutnya</Button>
              </div>
            </div>
          </>
        )}
    </div>
  );
}
